import { addMonths, format, parseISO, startOfMonth, endOfMonth, differenceInCalendarDays, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ActivityNode, ExecutionOverview, Workstream } from '../../../types/execution';

export const toISODate = (date: Date) => format(date, 'yyyy-MM-dd');

export const parseDate = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const date = parseISO(value);
    return isValid(date) ? date : null;
};

export const formatShortDate = (value: string | null | undefined) => {
    const date = parseDate(value);
    return date ? format(date, 'dd/MM', { locale: ptBR }) : '-';
};

export const formatDate = (value: string | null | undefined) => {
    const date = parseDate(value);
    return date ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : '-';
};

// Datas-objetivo podem ser ISO ou texto livre ("Q4", "TBC")
export const formatTargetDate = (value: string) => (parseDate(value) ? formatShortDate(value) : value);

export const formatPct = (value: number | null | undefined) =>
    value === null || value === undefined ? '-' : `${Math.round(value)}%`;

export const errorMessage = (error: any, fallback = 'Erro inesperado') =>
    error?.response?.data?.error || error?.message || fallback;

export interface TimelineRange {
    start: Date;
    end: Date;
    months: Date[];
}

const collectDates = (nodes: ActivityNode[], acc: Date[]) => {
    nodes.forEach((node) => {
        [node.planned_start, node.planned_end].forEach((value) => {
            const date = parseDate(value);
            if (date) acc.push(date);
        });
        collectDates(node.children, acc);
    });
};

/** Meses cobertos pelo cronograma (inclui a data de referência). */
export const buildTimeline = (workstreams: Workstream[], reportDate: string): TimelineRange => {
    const dates: Date[] = [];
    workstreams.forEach((ws) => collectDates(ws.phases, dates));
    const ref = parseDate(reportDate) || new Date();
    dates.push(ref);
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    const start = startOfMonth(min);
    const end = endOfMonth(max);
    const months: Date[] = [];
    for (let month = start; month <= end; month = addMonths(month, 1)) {
        months.push(month);
    }
    return { start, end, months };
};

export const timelineFromOverview = (overview: ExecutionOverview) =>
    buildTimeline(overview.workstreams, overview.report_date);

/** Posição (0-100%) de uma data dentro do intervalo do cronograma. */
export const timelinePosition = (range: TimelineRange, date: Date) => {
    const total = differenceInCalendarDays(range.end, range.start) + 1;
    const offset = differenceInCalendarDays(date, range.start);
    return Math.min(100, Math.max(0, (offset / total) * 100));
};

export const monthLabel = (month: Date) => {
    const label = format(month, 'MMM', { locale: ptBR });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

export const flattenActivities = (nodes: ActivityNode[]): ActivityNode[] =>
    nodes.flatMap((node) => [node, ...flattenActivities(node.children)]);
